import React, { useState, useEffect } from 'react';
import { Button, TextField, MenuItem, Typography, Container, Box, Grid, CssBaseline, Paper, Modal, Backdrop, Fade, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import { Google as GoogleIcon, Apple as AppleIcon, Visibility, VisibilityOff, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../432155.png';
import {ThemeContext} from '../context/ThemeContext';
import logo from '../logo.jpg';
import { Close } from '@mui/icons-material';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { darkMode } = React.useContext(ThemeContext);
  let navigate = useNavigate();
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
const [registerFirstName, setRegisterFirstName] = useState('');
const [registerLastName, setRegisterLastName] = useState('');
const [registerEmail, setRegisterEmail] = useState('');
const [registerPassword, setRegisterPassword] = useState('');
const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
const [registerClass, setRegisterClass] = useState('');
const [classes, setClasses] = useState([]);
const [registerError, setRegisterError] = useState('');
const [registerSuccess, setRegisterSuccess] = useState('');
const [openOTPModal, setOpenOTPModal] = useState(false);
const [otp, setOtp] = useState('');
const [otpError, setOtpError] = useState('');
const [timer, setTimer] = useState(60); // 1 minute in seconds
const [isTimerActive, setIsTimerActive] = useState(false);
const [isResendingOTP, setIsResendingOTP] = useState(false);



  const apiUrl = process.env.REACT_APP_BASE_URL; 

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1e3a8a',
      },
      secondary: {
        main: '#1e8bafff',
      },
      background: {
        default: '#ffffff',
      },
    },
  });


  useEffect(() => {
  if (openRegisterModal) {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(`${apiUrl}/student-classes/`);
        setClasses(response.data);
      } catch (error) {
        console.error('Failed to fetch classes:', error);
      }
    };
    fetchClasses();
  }
}, [openRegisterModal]);

useEffect(() => {
  let interval;
  if (isTimerActive && timer > 0) {
    interval = setInterval(() => {
      setTimer((prevTimer) => prevTimer - 1);
    }, 1000);
  } else if (timer === 0) {
    setIsTimerActive(false);
    setOtpError('OTP expired. Please request a new OTP.');
  }
  return () => clearInterval(interval);
}, [isTimerActive, timer]);

const handleOTPSubmit = async (event) => {
  event.preventDefault();
  setOtpError('');

  try {
    const response = await axios.post(`${apiUrl}/verify/`, {
      otp: otp,
    });

    setSnackbarMessage(response.data.message || 'Email verified successfully! You can now log in.');
    setSnackbarOpen(true);
    setOpenOTPModal(false);
    setOtp('');
    setIsTimerActive(false);
  } catch (error) {
    setOtpError(error.response?.data?.message || 'Invalid OTP. Please try again.');
  }
};



const handleRegisterSubmit = async (event) => {
  event.preventDefault();
  setRegisterError('');
  setRegisterSuccess('');

  if (registerPassword !== registerConfirmPassword) {
    setRegisterError("Passwords do not match.");
    return;
  }

  try {
    const response = await axios.post(`${apiUrl}/register/student/`, {
      email: registerEmail,
      first_name: registerFirstName,
      last_name: registerLastName,
      password: registerPassword,
      password2: registerConfirmPassword,
      student_class: registerClass,
    });

    setSnackbarMessage(response.data.message || 'Registration successful! Please verify your email.');
    setSnackbarOpen(true);
    setOpenRegisterModal(false);
    setOpenOTPModal(true);
    setIsTimerActive(true);
    setTimer(60);
  } catch (error) {
    if (error.response?.data?.email?.[0] === "user with this Email Address already exists.") {
      try {
        // Resend OTP for existing user
        await axios.post(`${apiUrl}/resend-otp/`, { email: registerEmail });
        setSnackbarMessage("This email is already registered. A new OTP has been sent for verification.");
        setSnackbarOpen(true);
        setOpenRegisterModal(false);
        setOpenOTPModal(true);
        setIsTimerActive(true);
        setTimer(60);
      } catch (resendError) {
        setSnackbarMessage("Failed to resend OTP. Please try again.");
        setSnackbarOpen(true);
      }
    } else {
      setRegisterError(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  }
};


const handleResendOTP = async () => {
  setOtpError('');
  try {
    const response = await axios.post(`${apiUrl}/resend-otp/`, {
      email: registerEmail,
    });

    setSnackbarMessage(response.data.message || 'OTP resent successfully!');
    setSnackbarOpen(true);
    setIsTimerActive(true);
    setTimer(60);
  } catch (error) {
    setOtpError(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
  }
};




  const handleSubmit = async (event) => {
  event.preventDefault();
  if (!email || !password) {
    setError("Please enter both email and password.");
    return;
  }
  try {
    const response = await axios.post(`${apiUrl}/login/`, { email, password });
    if (response.status === 200) {
      const { access_token, full_name, session_id, user_role } = response.data;
      sessionStorage.setItem("authToken", access_token);
      sessionStorage.setItem("fullName", full_name);
      sessionStorage.setItem("sessionId", session_id);
      sessionStorage.setItem("userRole", user_role);
      navigate("/home");
    } else {
      setError("Login failed. Please try again later.");
    }
  } catch (error) {
    if (error.response) {
      setError(error.response.data?.message || "Invalid email or password.");
    } else if (error.request) {
      setError("Unable to connect to the server. Check your internet.");
    } else {
      setError("An error occurred. Please try again later.");
    }
  }
};
 

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();
    setForgotPasswordError('');
    try {
      const token = sessionStorage.getItem("authToken");

      const response = await axios.post(`${apiUrl}/password-reset/`, {
        email: forgotPasswordEmail,
      }, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined
        }
      });

      console.log('Password Reset Email Sent:', response.data);
      setSnackbarMessage(response.data.message || 'We have sent you a link to reset your password');
      setSnackbarOpen(true);
      setForgotPasswordEmail('');
      setTimeout(() => {
        setOpenForgotPasswordModal(false);
      }, 2000);
    } catch (error) {
      console.error('Password Reset Failed:', error);
      setForgotPasswordError('Failed to send password reset email.');
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: -1,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
           "&::before": {  // Black overlay
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.21)",  // Adjust opacity here
      zIndex: -1,  // Places it behind the form
    },
        }}
      />
   <Container
  sx={{
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",  // Vertically centers the form
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
   
  }}
>
  <Paper
    elevation={24}
    sx={{
      padding: "20px",  // Reduced from "10px 20px" for tighter spacing
      width: "480px",
      display: "flex",
      justifyContent: "center",
      borderRadius: "20px",
      background: "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 8px 32px 0 rgba(30, 58, 138, 0.2)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
      component="form"
      onSubmit={handleSubmit}
    >
      {/* Logo Box (unchanged) */}
      <Box
        sx={{
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "15px",  // Reduced from 20px
          boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
          padding: "3px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ width: '90%', height: 'auto', objectFit: 'contain' }}
          />
        </Box>
      </Box>

      {/* Typography (unchanged) */}
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 700,
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          marginBottom: "5px",
        }}
      >
        Welcome Back
      </Typography>
      <Typography
        variant="body1"
        color="textSecondary"
        sx={{ marginBottom: "20px", fontWeight: 500 }}  // Reduced from 25px
      >
        Sign in to continue to Flashcard
      </Typography>

      {/* Error Message (unchanged) */}
   


      {/* TextFields (reduced margin) */}
      <TextField
        variant="outlined"
        margin="dense"  // Changed from "normal" to reduce vertical space
        required
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
         onChange={(e) => {
    setEmail(e.target.value);
    setError(""); // Clear error on input change
  }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailOutlined sx={{ color: "#1e3a8a" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: "12px",
            '&:hover fieldset': { borderColor: '#1e8bafff' },
            '&.Mui-focused fieldset': { borderColor: '#1e3a8a', borderWidth: '2px' },
          },
        }}
      />
      <TextField
        variant="outlined"
        margin="dense"  // Changed from "normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => {
    setPassword(e.target.value);
    setError(""); // Clear error on input change
  }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlined sx={{ color: "#1e3a8a" }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: "12px",
            '&:hover fieldset': { borderColor: '#1e8bafff' },
            '&.Mui-focused fieldset': { borderColor: '#1e3a8a', borderWidth: '2px' },
          },
        }}
      />

  {error && (
  <Typography
    variant="body2"
    color="error"
    align="center"
    sx={{
      marginBottom: "10px",
      fontWeight: 500,
      padding: "8px",
      borderRadius: "8px",
      width: "100%",
    }}
  >
    {error}
  </Typography>
)}
      {/* Button (reduced margin) */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          marginTop: "15px",  // Reduced from 20px
          padding: "10px",    // Reduced from 12px
          borderRadius: "12px",
          fontSize: "16px",
          fontWeight: 600,
          textTransform: "none",
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          boxShadow: "0 4px 15px rgba(30, 58, 138, 0.4)",
          transition: "all 0.3s ease",
          '&:hover': {
            boxShadow: "0 6px 20px rgba(30, 58, 138, 0.5)",
            transform: "translateY(-2px)",
          },
        }}
      >
        Sign In
      </Button>

      {/* Forgot Password Link (reduced margin) */}
      <Typography
        variant="body2"
        color="textSecondary"
        align="center"
        sx={{ marginTop: "10px", fontWeight: 500 }}  // Reduced from 15px
      >
        <span
          style={{
            cursor: "pointer",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontWeight: 600,
          }}
          onClick={() => setOpenForgotPasswordModal(true)}
        >
          Forgot password?
        </span>
      </Typography>
      <Typography
  variant="body2"
  color="textSecondary"
  align="center"
  sx={{ marginTop: "10px", fontWeight: 500 }}
>
  Don't have an account?{' '}
  <span
    style={{
      cursor: "pointer",
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      fontWeight: 600,
    }}
    onClick={() => setOpenRegisterModal(true)}
  >
    Register Now
  </span>
</Typography>


      {/* Privacy Policy Link (reduced padding/margin) */}
      <Box
        sx={{
          marginTop: "10px",  // Reduced from 15px
          paddingTop: "10px", // Reduced from 15px
          borderTop: "1px solid #e0e0e0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="textSecondary" align="center">
          <span
            style={{
              cursor: "pointer",
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 600,
            }}
            onClick={() => navigate("/privacy-policy")}
          >
            Privacy Policy
          </span>
        </Typography>
      </Box>
    </Box>
  </Paper>
</Container>


      {/* Forgot Password Modal */}
      <Modal
        aria-labelledby="forgot-password-modal-title"
        aria-describedby="forgot-password-modal-description"
        open={openForgotPasswordModal}
        onClose={() => {
          setOpenForgotPasswordModal(false);
          setForgotPasswordError("");
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
               backdropFilter: "blur(5px)",
            },
          },
        }}
      >
        <Fade in={openForgotPasswordModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 450,
              bgcolor: "background.paper",
              borderRadius: "20px",
              boxShadow: "0 8px 32px 0 rgba(30, 58, 138, 0.3)",
              p: 4,
              border: "none",
            }}
          >
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom 
              align="center"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                marginBottom: "15px",
              }}
            >
              Forgot Password
            </Typography>
            <IconButton
  onClick={() => {
    setOpenForgotPasswordModal(false);
    setForgotPasswordError("");
  }}
  sx={{
    position: "absolute",
    top: 8,
    right: 8,
    color: "#1e3a8a",
  }}
>
  <Close />
</IconButton>

            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ marginBottom: 25, fontWeight: 500 }}
            >
              Enter your registered email to receive a password reset link.
            </Typography>
            {forgotPasswordError && (
              <Typography
                variant="body2"
                color="error"
                align="center"
                style={{ marginBottom: 10 }}
              >
                {forgotPasswordError}
              </Typography>
            )}
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="forgot-password-email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: "12px",
                  '&:hover fieldset': {
                    borderColor: '#1e8bafff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e3a8a',
                    borderWidth: '2px',
                  },
                },
              }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleForgotPasswordSubmit}
              sx={{
                marginTop: "25px",
                padding: "14px",
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                boxShadow: "0 4px 15px rgba(30, 58, 138, 0.4)",
                transition: "all 0.3s ease",
                '&:hover': {
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  boxShadow: "0 6px 20px rgba(30, 58, 138, 0.5)",
                  transform: "translateY(-2px)",
                },
                '&:active': {
                  transform: "translateY(0)",
                },
              }}
            >
              Send Reset Link
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar for success message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Footer Text */}
<Box
  sx={{
    position: "fixed",
    bottom: 16,       // Distance from bottom
  //  left: "50%",      // Distance from right
   right: "40%",      // Distance from right
    zIndex: 1,       // Ensures it stays above other elements
    color: "rgba(233, 233, 233, 1)", // Adapts to dark/light mode
    fontSize: "0.65rem", // Small text
    fontWeight: "bolder",
  }}
>
  WARALS Technologies Pvt. Ltd. © Copyright 2020
</Box>

<Modal
  open={openRegisterModal}
  onClose={() => {
    setOpenRegisterModal(false);
    setRegisterError('');
  }}
  closeAfterTransition
  slots={{ backdrop: Backdrop }}
  slotProps={{
    backdrop: {
      timeout: 500,
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(5px)", // Add blur effect
      },
    },
  }}
>
  <Fade in={openRegisterModal}>
   <Box
  sx={{
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550, // Increased from 450
    bgcolor: "background.paper",
    borderRadius: "20px",
    boxShadow: "0 8px 32px 0 rgba(30, 58, 138, 0.3)",
    p: 4,
    border: "none",
  }}
>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        align="center"
        sx={{
          fontWeight: 700,
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          marginBottom: "15px",
        }}
      >
        Register
      </Typography>
      <IconButton
        onClick={() => {
          setOpenRegisterModal(false);
          setRegisterError('');
        }}
        sx={{ position: "absolute", top: 8, right: 8, color: "#1e3a8a" }}
      >
        <Close />
      </IconButton>

      {registerError && (
        <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
          {registerError}
        </Typography>
      )}

      <Box component="form" onSubmit={handleRegisterSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="First Name"
              value={registerFirstName}
              onChange={(e) => setRegisterFirstName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Last Name"
              value={registerLastName}
              onChange={(e) => setRegisterLastName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Email"
              type="email"
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type="password"
              value={registerConfirmPassword}
              onChange={(e) => setRegisterConfirmPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: "#1e3a8a" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Class"
              value={registerClass}
              onChange={(e) => setRegisterClass(e.target.value)}
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.name}>
                  {cls.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            mt: 3,
            mb: 2,
            padding: "12px",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: 600,
            textTransform: "none",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            boxShadow: "0 4px 15px rgba(30, 58, 138, 0.4)",
          }}
        >
          Register
        </Button>
      </Box>
    </Box>
  </Fade>
</Modal>

<Modal
  open={openOTPModal}
  onClose={() => {
    setOpenOTPModal(false);
    setOtpError('');
    setIsTimerActive(false);
    setTimer(60);
  }}
  closeAfterTransition
  slots={{ backdrop: Backdrop }}
  slotProps={{
    backdrop: {
      timeout: 500,
      sx: {
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(5px)",
      },
    },
  }}
>
  <Fade in={openOTPModal}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 550,
        bgcolor: "background.paper",
        borderRadius: "20px",
        boxShadow: "0 8px 32px 0 rgba(30, 58, 138, 0.3)",
        p: 4,
        border: "none",
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        align="center"
        sx={{
          fontWeight: 700,
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          marginBottom: "15px",
        }}
      >
        Verify Your Email
      </Typography>
      <IconButton
        onClick={() => {
          setOpenOTPModal(false);
          setOtpError('');
          setIsTimerActive(false);
          setTimer(60);
        }}
        sx={{ position: "absolute", top: 8, right: 8, color: "#1e3a8a" }}
      >
        <Close />
      </IconButton>

      <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
        We've sent a verification code to <strong>{registerEmail}</strong>. Please enter it below.
      </Typography>

      {otpError && (
        <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
          {otpError}
        </Typography>
      )}

      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        label="Enter OTP"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlined sx={{ color: "#1e3a8a" }} />
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
        Time remaining: {Math.floor(timer / 60)}:{timer % 60 < 10 ? `0${timer % 60}` : timer % 60}
      </Typography>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        onClick={handleOTPSubmit}
        sx={{
          mt: 3,
          mb: 2,
          padding: "12px",
          borderRadius: "12px",
          fontSize: "16px",
          fontWeight: 600,
          textTransform: "none",
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          boxShadow: "0 4px 15px rgba(30, 58, 138, 0.4)",
        }}
      >
        Verify OTP
      </Button>

     <Button
  fullWidth
  variant="text"
  onClick={handleResendOTP}
  disabled={isTimerActive || isResendingOTP}
  sx={{
    fontWeight: 600,
    color: "#1e3a8a",
  }}
>
  {isResendingOTP ? "Sending..." : "Resend OTP"}
</Button>

    </Box>
  </Fade>
</Modal>



    </ThemeProvider>
  );
};

export default Login;